import { z } from "zod";

export const TravauxBaseSchema = z.object({
  arbre_id: z.number().int().positive("L'ID de l'arbre doit être un nombre positif").nullable().optional(),
  essence_id: z.number().int().positive("L'ID de l'essence doit être un nombre positif").nullable().optional(),
  epaysage_id: z.number().int().positive("L'ID de l'espace boisé doit être un nombre positif").nullable().optional(),
  inventaire_id: z.number().int().positive("L'ID de l'inventaire doit être un nombre positif").nullable().optional(),
  type: z.string().max(255, "Le type ne peut pas dépasser 255 caractères").nullable().optional(),
  abattage: z.string().max(255, "L'abattage ne peut pas dépasser 255 caractères").nullable().optional(),
  travaux_collet_multiple: z.any().nullable().optional(), // JSON field
  travaux_tronc_multiple: z.any().nullable().optional(), // JSON field
  travaux_houppier_multiple: z.any().nullable().optional(), // JSON field
  travaux_commentaire: z.string().max(255, "Le commentaire ne peut pas dépasser 255 caractères").nullable().optional(),
  travaux_houppier_other: z.string().max(255, "Autres travaux houppier ne peuvent pas dépasser 255 caractères").nullable().optional(),
  travaux_collet_other: z.string().max(255, "Autres travaux collet ne peuvent pas dépasser 255 caractères").nullable().optional(),
  travaux_tronc_other: z.string().max(255, "Autres travaux tronc ne peuvent pas dépasser 255 caractères").nullable().optional(),
  travaux_tronc_protection: z.string().max(255, "La protection du tronc ne peut pas dépasser 255 caractères").nullable().optional(),
  travaux: z.any().nullable().optional(), // JSON field
  travaux_other: z.string().max(255, "Autres travaux ne peuvent pas dépasser 255 caractères").nullable().optional(),
  travaux_soin: z.string().max(255, "Les soins ne peuvent pas dépasser 255 caractères").nullable().optional(),
  travaux_protection: z.string().max(255, "La protection ne peut pas dépasser 255 caractères").nullable().optional(),
  nbre_sujet_concerne: z.number().int().positive("Le nombre de sujets concernés doit être positif").nullable().optional(),
  status_travaux: z.boolean().default(true).optional(),
  date_travaux: z.string().max(255, "La date des travaux ne peut pas dépasser 255 caractères").nullable().optional(),
  date_pro_visite: z.string().max(255, "La date de prochaine visite ne peut pas dépasser 255 caractères").nullable().optional(),
  user_edited_date_travaux: z.date().nullable().optional(),
});

export const TravauxSchema = TravauxBaseSchema.extend({
  id: z.number().int().positive(),
  created_at: z.date().nullable().optional(),
  updated_at: z.date().nullable().optional(),
});

export const TravauxCreateSchema = TravauxBaseSchema.pick({
  arbre_id: true,
  essence_id: true,
  epaysage_id: true,
  inventaire_id: true,
  type: true,
  abattage: true,
  travaux_collet_multiple: true,
  travaux_tronc_multiple: true,
  travaux_houppier_multiple: true,
  travaux_commentaire: true,
  travaux_houppier_other: true,
  travaux_collet_other: true,
  travaux_tronc_other: true,
  travaux_tronc_protection: true,
  travaux: true,
  travaux_other: true,
  travaux_soin: true,
  travaux_protection: true,
  nbre_sujet_concerne: true,
  status_travaux: true,
  date_travaux: true,
  date_pro_visite: true,
  user_edited_date_travaux: true,
});

export const TravauxUpdateSchema = z.object({
  id: z.number().int().positive(),
  arbre_id: z.number().int().positive("L'ID de l'arbre doit être un nombre positif").nullable().optional(),
  essence_id: z.number().int().positive("L'ID de l'essence doit être un nombre positif").nullable().optional(),
  epaysage_id: z.number().int().positive("L'ID de l'espace boisé doit être un nombre positif").nullable().optional(),
  inventaire_id: z.number().int().positive("L'ID de l'inventaire doit être un nombre positif").nullable().optional(),
  type: z.string().max(255, "Le type ne peut pas dépasser 255 caractères").nullable().optional(),
  abattage: z.string().max(255, "L'abattage ne peut pas dépasser 255 caractères").nullable().optional(),
  travaux_collet_multiple: z.any().nullable().optional(),
  travaux_tronc_multiple: z.any().nullable().optional(),
  travaux_houppier_multiple: z.any().nullable().optional(),
  travaux_commentaire: z.string().max(255, "Le commentaire ne peut pas dépasser 255 caractères").nullable().optional(),
  travaux: z.any().nullable().optional(),
  status_travaux: z.boolean().optional(),
  date_travaux: z.string().max(255, "La date des travaux ne peut pas dépasser 255 caractères").nullable().optional(),
  date_pro_visite: z.string().max(255, "La date de prochaine visite ne peut pas dépasser 255 caractères").nullable().optional(),
  user_edited_date_travaux: z.date().nullable().optional(),
});

export const TravauxListResponseSchema = z.object({
  data: z.array(TravauxSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export const TravauxResponseSchema = z.object({
  data: TravauxSchema,
  message: z.string().optional(),
});

export const TravauxSelectSchema = z.object({
  id: z.number().int().positive().optional(),
  arbre_id: z.number().int().positive().optional(),
  essence_id: z.number().int().positive().optional(),
  epaysage_id: z.number().int().positive().optional(),
  inventaire_id: z.number().int().positive().optional(),
  type: z.string().optional(),
  status_travaux: z.boolean().optional(),
  date_travaux: z.string().optional(),
});

export type Travaux = z.infer<typeof TravauxSchema>;
export type TravauxCreateInput = z.infer<typeof TravauxCreateSchema>;
export type TravauxUpdateInput = z.infer<typeof TravauxUpdateSchema>;
export type TravauxSelectInput = z.infer<typeof TravauxSelectSchema>;

// Form-specific types for frontend
export type TravauxFormData = Omit<TravauxCreateInput, 'status_travaux' | 'user_edited_date_travaux'> & {
  status_travaux?: boolean;
  user_edited_date_travaux?: Date;
};

export type TravauxEditFormData = Omit<TravauxUpdateInput, 'id'>;

// Utility types for different UI states
export type TravauxDisplayItem = Pick<Travaux, 'id' | 'type' | 'status_travaux' | 'date_travaux'>;
export type TravauxSelectOption = Pick<Travaux, 'id' | 'type'>;
export type TravauxTableItem = Pick<Travaux, 'id' | 'type' | 'abattage' | 'status_travaux' | 'date_travaux' | 'date_pro_visite' | 'created_at' | 'updated_at'>;

export type TravauxResponse = z.infer<typeof TravauxResponseSchema>;
export type TravauxListResponse = z.infer<typeof TravauxListResponseSchema>;

export const validateTravauxCreate = (data: unknown) => {
  return TravauxCreateSchema.safeParse(data);
};

export const validateTravauxUpdate = (data: unknown) => {
  return TravauxUpdateSchema.omit({ id: true }).safeParse(data);
};

export const validateTravauxSelect = (data: unknown) => {
  return TravauxSelectSchema.safeParse(data);
};
